import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  isDevMode,
  OnInit,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import type { ActionMenuItem } from '@seahorse/ui';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { SplitButtonModule } from 'primeng/splitbutton';
import { map } from 'rxjs';
import { primeSeverity, primeSize, ShSeverity, ShSize } from '../models';

/** `link`: looks like a text link (small inline actions, e.g. in a group header). */
export type ShButtonVariant = 'solid' | 'outlined' | 'text' | 'link';

/** Two menu models are the same when the PrimeNG-visible parts are equal. */
function sameMenuModel(a: MenuItem[], b: MenuItem[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (item, i) =>
        item.label === b[i].label &&
        item.icon === b[i].icon &&
        item.disabled === b[i].disabled &&
        item.styleClass === b[i].styleClass
    )
  );
}

/**
 * The button. A plain button, or, when `menu` is set, a split button whose
 * main part is the action and whose arrow opens the menu.
 *
 *   <sh-button label="shared.terms.save" (click)="save()" />
 *   <sh-button icon="fa-solid fa-plus" ariaLabel="shared.terms.add" variant="text" />
 *   <sh-button label="shared.terms.save" [menu]="saveAndAddMenu" (click)="save()" />
 *
 * There is no `click` output: the native click bubbles up from the real
 * button to the host, so `(click)` works as on any element (for the split
 * button only the main part, not the arrow). The same goes for
 * `type="submit"` inside a form.
 *
 * `label` and `ariaLabel` are translation keys or plain text. An icon-only
 * button needs an `ariaLabel`; it warns in development when it has neither.
 * The menu of the split button is appended to the body (see `appendTo`).
 */
@Component({
  selector: 'sh-button',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, TranslateModule, ButtonModule, SplitButtonModule],
  template: `
    <p-button
      *ngIf="!hasMenu(); else split"
      [type]="type()"
      [label]="label() | translate"
      [icon]="icon()"
      [iconPos]="iconPos()"
      [severity]="primeSeverity()"
      [outlined]="variant() === 'outlined'"
      [text]="variant() === 'text'"
      [link]="variant() === 'link'"
      [size]="primeSize()"
      [disabled]="disabled()"
      [loading]="loading()"
      [ariaLabel]="ariaLabel() | translate"
      [rounded]="rounded()"
      [autofocus]="autofocus()"
      [tabindex]="tabindex() ?? 0"
      [styleClass]="styleClass()"
    ></p-button>

    <ng-template #split>
      <p-splitbutton
        [label]="label() | translate"
        [icon]="icon()"
        [severity]="primeSeverity()"
        [outlined]="variant() === 'outlined'"
        [text]="variant() === 'text'"
        [size]="primeSize()"
        [disabled]="disabled()"
        [model]="menuModel()"
        [styleClass]="styleClass()"
        [appendTo]="appendTo()"
        (click)="keepMenuClicksInside($event)"
      ></p-splitbutton>
    </ng-template>
  `,
})
export class ShButtonComponent implements OnInit {
  readonly label = input<string>();
  /** Font Awesome classes. */
  readonly icon = input<string>();
  readonly iconPos = input<'left' | 'right' | 'top' | 'bottom'>('left');
  readonly severity = input<ShSeverity>('primary');
  readonly variant = input<ShButtonVariant>('solid');
  readonly size = input<ShSize>('md');
  readonly disabled = input(false, { transform: booleanAttribute });
  readonly loading = input(false, { transform: booleanAttribute });
  /** Required when there is no label. */
  readonly ariaLabel = input<string>();
  readonly type = input<'button' | 'submit'>('button');
  /** Fully rounded (icon-only buttons in headers and bars). */
  readonly rounded = input(false, { transform: booleanAttribute });
  /** Focused when a dialog or overlay opens (PrimeNG's autofocus). */
  readonly autofocus = input(false, { transform: booleanAttribute });
  /** Unset: 0 (PrimeNG turns an unset tabindex into "NaN"). */
  readonly tabindex = input<number>();
  /** Escape hatch for a central class hook (e.g. `dialog-close`); no styling of its own. */
  readonly styleClass = input<string>();
  /**
   * Where the split button's menu is appended: the body by default; `self`
   * keeps it inline (needed inside an ng-bootstrap modal, which stacks above
   * the body-level overlays).
   */
  readonly appendTo = input<'body' | 'self' | HTMLElement>('body');
  /** Turns the button into a split button. */
  readonly menu = input<ActionMenuItem<void>[]>();

  private readonly _translate = inject(TranslateService);
  private readonly _lang = toSignal(
    this._translate.onLangChange.pipe(map((e) => e.lang)),
    { initialValue: this._translate.currentLang }
  );

  protected readonly primeSeverity = computed(() =>
    primeSeverity(this.severity())
  );
  protected readonly primeSize = computed(() => primeSize(this.size()));

  // Only the visible items, translated. Recomputed when `menu` (or the
  // language) changes; a page that builds the array in a getter hands over a
  // new one every change detection, so a structurally equal model keeps the
  // previous instance (a new model each time would re-render the PrimeNG
  // split button endlessly). Commands go through the current `menu()` by
  // index, so they never run a stale closure.
  protected readonly menuModel = computed<MenuItem[]>(
    () => {
      this._lang();
      const items: MenuItem[] = [];
      (this.menu() ?? []).forEach((item, index) => {
        const visible =
          typeof item.visible === 'function'
            ? item.visible(undefined)
            : item.visible !== false;
        if (!visible) return;
        const label =
          typeof item.label === 'function' ? item.label(undefined) : item.label;
        items.push({
          label: this._translate.instant(label),
          icon: item.icon,
          disabled:
            typeof item.disabled === 'function'
              ? item.disabled(undefined)
              : !!item.disabled,
          styleClass: item.danger ? 'p-menuitem-danger' : undefined,
          command: () => this.menu()?.[index]?.command(undefined),
        });
      });
      return items;
    },
    { equal: sameMenuModel }
  );

  ngOnInit(): void {
    if (isDevMode() && !this.label() && !this.ariaLabel()) {
      console.warn(
        'sh-button without a label needs an ariaLabel (icon-only button).'
      );
    }
  }

  protected hasMenu(): boolean {
    return (this.menu()?.length ?? 0) > 0;
  }

  // The arrow of a split button is not the button's action: a host
  // (click) must only fire for the main part.
  protected keepMenuClicksInside(event: Event): void {
    if ((event.target as HTMLElement).closest('.p-splitbutton-dropdown')) {
      event.stopPropagation();
    }
  }
}
